import { MockAIProvider } from "@/ai/mock";
import type {
  ActionResult,
  JiaxingCase,
  KnowledgeCard,
  NextQuestion,
  PublicStats,
  QuestionAnalysis,
  ReflectionResult,
  Topic,
} from "@/types/journey";
import { createLocalSessionId } from "@/lib/ids";
import { cleanUserText } from "@/lib/text";
import { pickCaseFor, type CaseMatchInput } from "@/lib/caseMatch";

export type ApiSource = "api" | "local-mock" | "local-fallback";

const LAST_CASE_KEY = "red-boat-last-case";

function readLastCaseId(): string | null {
  try {
    return window.localStorage.getItem(LAST_CASE_KEY);
  } catch {
    return null;
  }
}

function rememberCaseId(id: string): void {
  try {
    window.localStorage.setItem(LAST_CASE_KEY, id);
  } catch {
    /* 隐私模式下忽略 */
  }
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status = 0,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

interface ApiEnvelope<T> {
  data: T;
  source?: "openai" | "mock" | "fallback";
}

export interface Sourced<T> {
  value: T;
  source: ApiSource;
}

const localMock = new MockAIProvider();

/* AI 超时契约（与 worker/index.ts 的 AI_REQUEST_BUDGET_MS、src/ai/deepseek.ts 三层配套）：
   服务端单请求上界 12s，客户端等 15s——晚于服务端，所以正常情况下是服务端自己的
   兜底 payload（source: "fallback"）先返回，而不是客户端抢先把请求掐断。
   这个值只用于兜住网络断流；实测生产 deepseek-chat 两条路径各约 2s。 */
const REQUEST_TIMEOUT_MS = 15_000;

async function requestEnvelope<T>(path: string, init: RequestInit, timeoutMs = 9000): Promise<ApiEnvelope<T>> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(path, {
      ...init,
      signal: controller.signal,
      headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    });
    if (!response.ok) throw new ApiClientError("REQUEST_FAILED", response.status);
    const payload = (await response.json()) as ApiEnvelope<T> | T;
    if (typeof payload === "object" && payload !== null && "data" in payload) {
      return payload;
    }
    return { data: payload as T };
  } catch (error) {
    if (error instanceof ApiClientError) throw error;
    throw new ApiClientError(error instanceof Error && error.name === "AbortError" ? "REQUEST_TIMEOUT" : "NETWORK_OFFLINE");
  } finally {
    window.clearTimeout(timer);
  }
}

async function request<T>(path: string, init: RequestInit, timeoutMs = 9000): Promise<T> {
  const envelope = await requestEnvelope<T>(path, init, timeoutMs);
  return envelope.data;
}

export class JourneyApi {
  async track(sessionId: string, event: "ticket_generated"): Promise<void> {
    try {
      await request<{ recorded: true }>("/api/event", {
        method: "POST",
        body: JSON.stringify({ sessionId, event }),
      }, 3000);
    } catch {
      // Analytics is deliberately best-effort and never blocks the journey.
    }
  }

  async startSession(): Promise<string> {
    try {
      const result = await request<{ sessionId: string }>("/api/session", {
        method: "POST",
        body: JSON.stringify({}),
      });
      return result.sessionId;
    } catch {
      return createLocalSessionId();
    }
  }

  async analyzeQuestion(sessionId: string, question: string): Promise<Sourced<QuestionAnalysis>> {
    const safeQuestion = cleanUserText(question);
    try {
      const envelope = await requestEnvelope<QuestionAnalysis>("/api/question", {
        method: "POST",
        body: JSON.stringify({ sessionId, question: safeQuestion }),
      }, REQUEST_TIMEOUT_MS);
      return { value: envelope.data, source: envelope.source === "mock" ? "local-mock" : envelope.source === "fallback" ? "local-fallback" : "api" };
    } catch {
      return { value: await localMock.analyzeQuestion(safeQuestion), source: "local-fallback" };
    }
  }

  async nextQuestion(sessionId: string, topic: Topic, selectedOption: "A" | "B"): Promise<Sourced<NextQuestion>> {
    try {
      const envelope = await requestEnvelope<NextQuestion>("/api/answer", {
        method: "POST",
        body: JSON.stringify({ sessionId, stage: 1, selectedOption, topic }),
      }, REQUEST_TIMEOUT_MS);
      return { value: envelope.data, source: envelope.source === "mock" ? "local-mock" : envelope.source === "fallback" ? "local-fallback" : "api" };
    } catch {
      return { value: await localMock.generateNextQuestion({ topic, selectedOption }), source: "local-fallback" };
    }
  }

  async reflection(input: {
    sessionId: string;
    originalQuestion: string;
    firstAnswer: string;
    secondAnswer: string;
    thirdAnswer: string;
  }): Promise<Sourced<ReflectionResult>> {
    try {
      /* 反思最长（100—180 字），但与其它 AI 路径共用同一客户端预算：
         它比服务端的 12s 上界更宽，真正的兜底由服务端先返回。 */
      const envelope = await requestEnvelope<ReflectionResult>("/api/result", {
        method: "POST",
        body: JSON.stringify(input),
      }, REQUEST_TIMEOUT_MS);
      return { value: envelope.data, source: envelope.source === "mock" ? "local-mock" : envelope.source === "fallback" ? "local-fallback" : "api" };
    } catch {
      return {
        value: await localMock.generateReflection(input),
        source: "local-fallback",
      };
    }
  }

  async refineAction(sessionId: string, original: string): Promise<Sourced<ActionResult>> {
    const safeOriginal = cleanUserText(original);
    try {
      const envelope = await requestEnvelope<ActionResult>("/api/action", {
        method: "POST",
        body: JSON.stringify({ sessionId, original: safeOriginal }),
      }, REQUEST_TIMEOUT_MS);
      return { value: envelope.data, source: envelope.source === "mock" ? "local-mock" : envelope.source === "fallback" ? "local-fallback" : "api" };
    } catch {
      return { value: await localMock.refineAction(safeOriginal), source: "local-fallback" };
    }
  }

  async complete(input: {
    sessionId: string;
    topic: Topic;
    actionKeyword: string;
    caseId: string | null;
  }): Promise<Sourced<PublicStats>> {
    try {
      const envelope = await requestEnvelope<PublicStats>("/api/complete", {
        method: "POST",
        body: JSON.stringify({ ...input, journeyCompleted: true }),
      });
      return { value: envelope.data, source: envelope.source === "mock" ? "local-mock" : envelope.source === "fallback" ? "local-fallback" : "api" };
    } catch {
      const key = "red-boat-local-completed";
      const count = Number(window.localStorage.getItem(key) ?? "0") + 1;
      window.localStorage.setItem(key, String(count));
      return { value: { completed: count, updatedAt: new Date().toISOString() }, source: "local-fallback" };
    }
  }

  async publishedKnowledge(theme?: string): Promise<KnowledgeCard[]> {
    try {
      return await request<KnowledgeCard[]>(`/api/public/knowledge${theme ? `?theme=${encodeURIComponent(theme)}` : ""}`, {
        method: "GET",
      });
    } catch {
      return [];
    }
  }

  /** 依据用户的问题与三次回答，从正式案例库里挑出最贴的一条。 */
  async publishedCase(topic: string, context: CaseMatchInput = {}): Promise<JiaxingCase | null> {
    try {
      const all = await request<JiaxingCase[]>("/api/public/cases", { method: "GET" });
      const chosen = pickCaseFor(all, { ...context, topic: context.topic ?? topic }, Math.random, readLastCaseId());
      if (chosen) rememberCaseId(chosen.id);
      return chosen;
    } catch {
      return null;
    }
  }

  async publicStats(): Promise<PublicStats> {
    try {
      return await request<PublicStats>("/api/public/stats", { method: "GET" });
    } catch {
      return { completed: Number(window.localStorage.getItem("red-boat-local-completed") ?? "0"), updatedAt: new Date().toISOString() };
    }
  }
}
